import type { HexTileMap, HexTileMapKey } from '../hexmap/model/types';
import type { RuleSet } from '../rules/schema';
import {
  type Building,
  buildingLevel,
  type Cell,
  type Command,
  canAssign,
  canFoundCity,
  canPlace,
  canPromote,
  canSpawn,
  canTrain,
  canUpgrade,
  covers,
  exposeCooldownLeft,
  type FoundCityCheck,
  isCity,
  isMilitary,
  type PlayerId,
  promoteCandidates,
  type SimState,
  type UnitType,
  unitRank,
} from '../sim';
import { promotedText, unitName, unitsText } from './promotions';
import {
  assignRejectionText,
  barracksFullText,
  foundCityRejectionText,
  hiddenCityText,
  placeRejectionText,
  promoteRejectionText,
  spawnRejectionText,
  trainRejectionText,
  upgradeRejectionText,
  wrongExposeText,
} from './reasons';
import { stockInUnits } from './resources';

/**
 * Tells the player when the sim refused a click. Buttons are enabled from
 * the last snapshot, so a click can still be refused by the host (e.g. a
 * second Train click before the snapshot showing the first one's cost
 * arrives, or a city hidden by fog too close to a settler). Each tracked
 * command remembers what the state looked like when it was sent; once its
 * effect shows up it's dropped, and if it hasn't within FEEDBACK_TICKS of
 * sim time it's reported as refused, with the reason the shared checks give
 * now. A command the checks still allow isn't reported: the view may just lag
 * the host (a peer's snapshots arriving late, so the command reaches the host
 * many ticks after the tick it was sent on), so it waits up to
 * FEEDBACK_MAX_TICKS for its effect or a reason and is then dropped silently.
 * A refusal never reads without a reason. Placed sites are confirmed too.
 * Pure (no React).
 */

/** Sim ticks (100 ms each) a command gets to show its effect before it counts as refused. */
export const FEEDBACK_TICKS = 15;

/**
 * Sim ticks a command the checks still allow waits for its effect (or for a
 * reason to show up) before it's dropped without a word.
 */
export const FEEDBACK_MAX_TICKS = 100;

export interface PendingCommand {
  command: Command;
  /** Sim tick when it was sent. */
  tick: number;
  /** Ids minted after this belong to units/buildings made since (ids only grow). */
  nextEntityId: number;
  /** Level of the building being upgraded, when sent. */
  level?: number;
  /** Whether that building was already upgrading when sent (a repeat click doesn't land). */
  upgrading?: boolean;
  /**
   * Units being trained by id: the xp each reaches once its next rank lands
   * (Infinity for one already at the top rank, which never lands: refused).
   */
  xp?: Record<string, number>;
  /** Units being promoted (not yet of the target type) by id: their type when sent. */
  from?: Record<string, UnitType>;
  /**
   * A city-wide promote: how many of `from` (its candidates when sent) it
   * should promote; it has landed once that many have been.
   */
  want?: number;
  /**
   * Units of `from` already counted as promoted by this command (and put in
   * `claimed`, so no later pending promote counts them again).
   */
  promoted?: string[];
  /**
   * The first refusal reason the checks gave while it waited, reported once
   * time is up even if the condition has cleared since (stock unloaded, a
   * tile freed). A lagging view shows more stock and room, never less, so a
   * reason seen is a real shortfall.
   */
  reason?: string;
}

/**
 * The units a command orders (none for `expose`: the unit it picks is the
 * suspect, not one of the sender's). A caller that knows some of them aren't
 * really the player's (a spy disguised as theirs) doesn't track the command,
 * so no refusal gives the spy away.
 */
export function commandUnitIds(command: Command): string[] {
  if (command.type === 'expose') return [];
  if ('unitIds' in command) return command.unitIds;
  if ('unitId' in command) return [command.unitId];
  return [];
}

/**
 * A pending entry for commands whose effect can be checked; null for the
 * rest. An `expose` needs the sender's `playerId` (it's tracked unless
 * they're already waiting out a cooldown, when the sim ignores it).
 */
export function trackCommand(
  state: SimState,
  command: Command,
  rules?: RuleSet,
  playerId?: PlayerId
): PendingCommand | null {
  const base = { command, tick: state.tick, nextEntityId: state.nextEntityId };
  switch (command.type) {
    case 'expose':
      return playerId !== undefined && exposeCooldownLeft(state, playerId) === 0 ? base : null;
    case 'spawn':
    case 'place':
    case 'foundCity':
      return base;
    case 'assign':
      // A nudge leaves the worker automated: nothing to confirm.
      return command.pin === false ? null : base;
    case 'upgrade': {
      const b = state.buildings[command.buildingId];
      return b ? { ...base, level: buildingLevel(b), upgrading: !!b.upgrading } : null;
    }
    case 'train': {
      if (!rules) return null;
      const xp: Record<string, number> = {};
      for (const id of trainIds(command)) {
        const u = state.units[id];
        // At the top rank: it never lands, so the click is reported ("Top rank").
        if (u) xp[id] = rules.veterancy.ranks[unitRank(rules, u)] ?? Number.POSITIVE_INFINITY;
      }
      return Object.keys(xp).length > 0 ? { ...base, xp } : null;
    }
    case 'promote': {
      const from: Record<string, UnitType> = {};
      if (!('unitIds' in command)) {
        // A city's best units: any of its candidates may be the one picked.
        if (!rules || playerId === undefined) return null;
        const units = promoteCandidates(
          state,
          rules,
          playerId,
          command.cityId,
          command.to,
          command.from
        );
        for (const u of units) from[u.id] = u.type;
        const count = command.count ?? 1;
        const want = Math.min(units.length, count === 'all' ? units.length : count);
        return want > 0 ? { ...base, from, want } : null;
      }
      for (const id of command.unitIds) {
        const u = state.units[id];
        if (u && u.type !== command.to) from[id] = u.type;
      }
      return Object.keys(from).length > 0 ? { ...base, from } : null;
    }
    default:
      return null;
  }
}

const idNumber = (id: string) => Number.parseInt(id.slice(1), 10);

/** The units a `train` command trains: one, or several. */
function trainIds(command: Extract<Command, { type: 'train' }>): string[] {
  return 'unitIds' in command ? [...new Set(command.unitIds)] : [command.unitId];
}

/** A train command's units still short of the rank they were sent for (alive). */
function trainHeld(state: SimState, p: PendingCommand): string[] {
  return Object.entries(p.xp ?? {}).flatMap(([id, need]) => {
    const u = state.units[id];
    return u && (u.xp ?? 0) < need ? [id] : [];
  });
}

/**
 * Whether the command's effect is in `state`. A spawn or city founding is
 * matched to one new unit or city not already in `claimed` (and claims it),
 * so two Train clicks need two new units: when only one lands, the other is
 * reported. A unit that trains (units.yaml `trainTime`) lands once it's in
 * the city's training queue: it can take longer than FEEDBACK_TICKS to come
 * out, and is reported then by the notifications ("Warrior trained").
 */
function commandLanded(
  state: SimState,
  rules: RuleSet,
  playerId: PlayerId,
  p: PendingCommand,
  claimed: Set<string>
): boolean {
  const c = p.command;
  const isNew = (id: string) => idNumber(id) >= p.nextEntityId && !claimed.has(id);
  const claim = (id: string | undefined) => {
    if (id === undefined) return false;
    claimed.add(id);
    return true;
  };
  switch (c.type) {
    case 'spawn': {
      const queued = state.buildings[c.cityId]?.training?.find(
        (t) => t.unitType === c.unitType && isNew(t.id)
      );
      return claim(
        queued?.id ??
          Object.values(state.units).find(
            (u) => u.owner === playerId && u.type === c.unitType && isNew(u.id)
          )?.id
      );
    }
    case 'place':
      return Object.values(state.buildings).some(
        (b) =>
          b.owner === playerId &&
          b.type === c.buildingType &&
          b.pos.q === c.target.q &&
          b.pos.r === c.target.r &&
          idNumber(b.id) >= p.nextEntityId
      );
    case 'foundCity':
      return claim(
        Object.values(state.buildings).find(
          (b) => b.owner === playerId && isCity(b, rules) && isNew(b.id)
        )?.id
      );
    case 'upgrade': {
      const b = state.buildings[c.buildingId];
      return !!b && (buildingLevel(b) > (p.level ?? 1) || (!p.upgrading && !!b.upgrading));
    }
    case 'assign': {
      // Some unit holds a pinned order at the target (or at the building on it).
      const cell = typeof c.target === 'string' ? state.buildings[c.target]?.pos : c.target;
      const cover = Object.values(state.buildings).find(
        (b) => b.id === c.target || (cell && covers(b, cell))
      );
      return c.unitIds.some((id) => {
        const o = state.units[id]?.order;
        if (o?.kind === 'build') return o.pin === true && o.buildingId === cover?.id;
        if (o?.kind !== 'gather' || !o.pin || !cell) return false;
        const at = cover?.pos ?? cell;
        return o.target.q === at.q && o.target.r === at.r;
      });
    }
    case 'train':
      // Every unit ranked up (one that died meanwhile has nothing to report).
      return trainHeld(state, p).length === 0;
    case 'promote':
      // Every unit promoted (or dead, which has nothing to report).
      return !promoteShort(promoteOutcome(state, p, claimed));
    default:
      return true;
  }
}

/**
 * A promote command's units that became the target type, and those that
 * haven't (alive). A unit promoted is counted by one pending command only:
 * the first (oldest) to see it claims it (`claimed`), so fast repeated
 * clicks of a city's chip never all count the same units; a city-wide one
 * counts at most `want`. For a city-wide one, held are the unclaimed
 * candidates still short of `want` (none once that many are promoted).
 */
function promoteOutcome(
  state: SimState,
  p: PendingCommand,
  claimed: Set<string>
): { promoted: string[]; held: string[] } {
  const promoted = [...(p.promoted ?? [])];
  const held: string[] = [];
  if (p.command.type !== 'promote') return { promoted, held };
  const own = new Set(promoted);
  for (const id of Object.keys(p.from ?? {})) {
    const u = state.units[id];
    if (!u || own.has(id) || claimed.has(id)) continue;
    if (u.type !== p.command.to) held.push(id);
    else if (p.want === undefined || promoted.length < p.want) {
      promoted.push(id);
      claimed.add(id);
    }
  }
  if (p.want !== undefined)
    return { promoted, held: held.slice(0, Math.max(0, p.want - promoted.length)) };
  return { promoted, held };
}

/**
 * Whether a promote command is still short of what it asked for: some of its
 * units (a city-wide one: unclaimed candidates, up to `want`) are still
 * unpromoted. A city-wide one whose candidates were all taken by earlier
 * clicks got everything they still allowed, so it isn't short.
 */
function promoteShort(o: { promoted: string[]; held: string[] }): boolean {
  return o.held.length > 0;
}

/**
 * Why the command was refused, from the shared checks against `state`; null
 * when they now pass (no reason to give). `knownBuilding` is the player's fog-safe
 * view of buildings, so a city founding refused by a hidden city doesn't
 * reveal how far away it is.
 */
function refusalText(
  state: SimState,
  ctx: { terrain: HexTileMap; rules: RuleSet },
  playerId: PlayerId,
  command: Command,
  knownBuilding?: (key: HexTileMapKey) => Building | null
): string | null {
  const { rules } = ctx;
  switch (command.type) {
    case 'spawn': {
      const check = canSpawn(state, ctx, playerId, command.cityId, command.unitType);
      const name = rules.units[command.unitType]?.name ?? command.unitType;
      return check.ok
        ? null
        : `Couldn't train ${name}: ${spawnRejectionText(state, rules, playerId, command.unitType, check.reason, command.cityId)}`;
    }
    case 'place': {
      const check = canPlace(state, ctx, playerId, command.buildingType, command.target);
      const name = rules.buildings[command.buildingType]?.name ?? command.buildingType;
      return check.ok
        ? null
        : `Couldn't build ${name}: ${placeRejectionText(rules, command.buildingType, check.reason, stockInUnits(state, playerId))}`;
    }
    case 'foundCity': {
      // Judged from what the player knows first, so a hidden city isn't placed.
      const check = canFoundCity(state, ctx, playerId, command.unitId, knownBuilding);
      if (!check.ok) return `Couldn't found a city: ${foundCityRejectionText(rules, check)}`;
      const real = canFoundCity(state, ctx, playerId, command.unitId);
      // Both checks pass: the order just hasn't landed in this view yet (a lagging peer).
      if (real.ok) return null;
      return `Couldn't found a city: ${real.reason === 'too-close' ? hiddenCityText(rules) : foundCityRejectionText(rules, real)}`;
    }
    case 'upgrade': {
      const b = state.buildings[command.buildingId];
      const check = canUpgrade(state, ctx, playerId, command.buildingId);
      if (!b || check.ok) return null;
      // Never a refusal without a reason.
      const text =
        upgradeRejectionText(state, rules, b, playerId, check) ?? "Can't level up this building";
      return `Couldn't level up: ${text}`;
    }
    case 'assign': {
      const check = canAssign(state, ctx, playerId, command.unitIds, command.target);
      return check.ok ? null : `Couldn't send workers: ${assignRejectionText(check.reason)}`;
    }
    case 'train': {
      const ids = trainIds(command).filter((id) => state.units[id]);
      for (const id of ids) {
        const u = state.units[id];
        const check = canTrain(state, rules, playerId, id);
        if (check.ok) continue;
        // "Couldn't rank up 3 Archers: …" for several, as the button reads.
        const what =
          ids.length > 1
            ? ` ${unitsText(
                rules,
                ids.map((i) => state.units[i].type)
              )}`
            : '';
        return `Couldn't rank up${what}: ${trainRejectionText(state, rules, u, check.reason)}`;
      }
      return null;
    }
    case 'promote': {
      if (!('unitIds' in command)) return null;
      const held = command.unitIds.flatMap((id) => {
        const u = state.units[id];
        return u && u.owner === playerId && u.type !== command.to ? [u] : [];
      });
      const first = held[0];
      // "Couldn't upgrade 4 Workers to Labourers", as the confirmation reads.
      const toName = unitName(rules, command.to, Math.max(1, held.length));
      if (!first) return null;
      const what = unitsText(
        rules,
        held.map((u) => u.type)
      );
      const check = canPromote(state, ctx, first, command.to);
      return check.ok
        ? null
        : `Couldn't upgrade ${what} to ${toName}: ${promoteRejectionText(state, rules, first, command.to, check.reason)}`;
    }
    default:
      return null;
  }
}

/**
 * Splits pending commands (oldest first) into those still waiting, the
 * refusal texts of those that ran out of time without landing, a short
 * confirmation for each site that was placed ("Farm placed") and each
 * promotion ("3 Archers upgraded to Rifle Squads"), and notes for
 * commands that landed differently than asked (a soldier trained while its
 * city's barracks were full waits outside: "Barracks full (8/8): Warrior
 * waits outside"). A command past FEEDBACK_TICKS is reported with the
 * reason the checks give now, else the first one they gave while it waited
 * (`PendingCommand.reason`); one they never refused keeps waiting until
 * FEEDBACK_MAX_TICKS, then is dropped without a toast. `claimed` holds the ids of units and cities already
 * matched to an earlier command (new units and cities, and promoted units);
 * keep it across calls. `knownBuilding` as in
 * `refusalText`.
 */
export function settleCommands(
  state: SimState,
  ctx: { terrain: HexTileMap; rules: RuleSet },
  playerId: PlayerId,
  pending: readonly PendingCommand[],
  claimed: Set<string>,
  knownBuilding?: (key: HexTileMapKey) => Building | null
): { waiting: PendingCommand[]; refused: string[]; placed: string[]; notes: string[] } {
  const waiting: PendingCommand[] = [];
  const refused: string[] = [];
  const placed: string[] = [];
  const notes: string[] = [];
  for (const p of pending) {
    const c0 = p.command;
    if (c0.type === 'expose') {
      // A false accusation starts the sender's cooldown; a spy caught is
      // reported by the notifications ("Spy exposed!").
      const until = state.players[playerId]?.exposeCooldownUntil;
      if (until !== undefined && until > p.tick) {
        const text = wrongExposeText(ctx.rules);
        if (!refused.includes(text)) refused.push(text);
      } else if (!state.units[c0.unitId]?.exposed && state.tick - p.tick <= FEEDBACK_TICKS) {
        waiting.push(p);
      }
      continue;
    }
    if (c0.type === 'promote') {
      // Promoted units are confirmed ("3 Archers upgraded to Rifle Squads"); once time
      // is up, the rest are reported as refused.
      const outcome = promoteOutcome(state, p, claimed);
      const { promoted, held } = outcome;
      const short = promoteShort(outcome);
      const elapsed = state.tick - p.tick;
      const now =
        short && elapsed > 0
          ? refusalText(
              state,
              ctx,
              playerId,
              { type: 'promote', unitIds: held, to: c0.to },
              knownBuilding
            )
          : null;
      const why = elapsed > FEEDBACK_TICKS ? (now ?? p.reason ?? null) : null;
      // Still waiting: in time, or the checks still allow it (a lagging view).
      if (short && !why && elapsed <= FEEDBACK_MAX_TICKS) {
        const counted = promoted.length > (p.promoted?.length ?? 0) ? { promoted } : {};
        const reason = now && !p.reason ? { reason: now } : {};
        waiting.push({ ...p, ...counted, ...reason });
        continue;
      }
      if (promoted.length > 0) {
        placed.push(
          promotedText(
            ctx.rules,
            promoted.map((id) => p.from?.[id] ?? c0.to),
            c0.to
          )
        );
      }
      if (why) refused.push(why);
      continue;
    }
    const before = claimed.size;
    if (commandLanded(state, ctx.rules, playerId, p, claimed)) {
      const c = p.command;
      if (c.type === 'place')
        placed.push(`${ctx.rules.buildings[c.buildingType]?.name ?? c.buildingType} placed`);
      if (c.type === 'spawn' && claimed.size > before) {
        const unit = state.units[[...claimed][claimed.size - 1]];
        const full =
          unit && !unit.inside && isMilitary(ctx.rules, unit.type)
            ? barracksFullText(state, ctx.rules, c.cityId, c.unitType)
            : null;
        if (full)
          notes.push(`${full}: ${ctx.rules.units[c.unitType]?.name ?? c.unitType} waits outside`);
      }
      continue;
    }
    const elapsed = state.tick - p.tick;
    // A train is judged on the units it hasn't ranked up yet.
    const command: Command =
      p.command.type === 'train' ? { type: 'train', unitIds: trainHeld(state, p) } : p.command;
    const now = elapsed > 0 ? refusalText(state, ctx, playerId, command, knownBuilding) : null;
    const why = elapsed > FEEDBACK_TICKS ? (now ?? p.reason ?? null) : null;
    if (why) refused.push(why);
    else if (elapsed <= FEEDBACK_MAX_TICKS)
      waiting.push(now && !p.reason ? { ...p, reason: now } : p);
  }
  return { waiting, refused, placed, notes };
}

/** The player's settlers with a `found` order (`foundAt`), by id: the tile each is headed for. */
export function foundOrders(state: SimState, playerId: PlayerId): Map<string, Cell> {
  const out = new Map<string, Cell>();
  for (const u of Object.values(state.units)) {
    if (u.owner === playerId && u.order?.kind === 'found') out.set(u.id, u.order.target);
  }
  return out;
}

/**
 * Why each of the settlers in `watched` (`foundOrders` of an earlier state)
 * stopped without founding a city: its `found` order is gone, it's still
 * alive and it isn't walking off on a new order (a settler that founded is
 * gone; one given a new move has a path or another order). Founding on
 * arrival can take minutes, past FEEDBACK_TICKS, so this is checked every
 * state instead. The reason is the one the sim recorded for that settler
 * (`PlayerState.lastFoundFailure`) at or after `sinceTick` (the watched
 * state's tick, so an older, already-reported failure isn't reused), else
 * what the checks say now; either way
 * told fog-safe: `knownBuilding` as in `refusalText`, so a hidden city only
 * reads as `hiddenCityText`. `cityName` names a known city in the way
 * ("too close to City 2").
 */
export function droppedFoundOrders(
  state: SimState,
  ctx: { terrain: HexTileMap; rules: RuleSet },
  playerId: PlayerId,
  watched: ReadonlyMap<string, Cell>,
  knownBuilding?: (key: HexTileMapKey) => Building | null,
  cityName?: (cityId: string) => string | null,
  sinceTick = 0
): string[] {
  const texts: string[] = [];
  const failure = state.players[playerId]?.lastFoundFailure;
  for (const [id, target] of watched) {
    const u = state.units[id];
    if (!u || u.owner !== playerId || u.order || u.path) continue;
    const recorded = failure?.unitId === id && failure.tick >= sinceTick ? failure : undefined;
    const at = recorded?.target ?? target;
    const known = canFoundCity(state, ctx, playerId, id, knownBuilding, at);
    const real = known.ok ? canFoundCity(state, ctx, playerId, id, undefined, at) : known;
    const rejection = (check: Extract<FoundCityCheck, { ok: false }>) =>
      lowerFirst(foundCityRejectionText(ctx.rules, check, cityName));
    const unreachable = "the settler couldn't reach that tile";
    let why: string | null;
    switch (recorded?.reason) {
      case 'unreachable':
        why = unreachable;
        break;
      case 'taken':
        why = 'your units hold that tile and no tile next to it will do';
        break;
      case 'too-close':
        why = known.ok ? lowerFirst(hiddenCityText(ctx.rules)) : rejection(known);
        break;
      case 'occupied':
      case 'unknown':
        why = u.inside
          ? 'the settler is inside a building'
          : !known.ok && known.reason !== 'unknown'
            ? rejection(known)
            : null;
        break;
      default:
        // Nothing recorded for it: judge from the state now.
        why = !known.ok
          ? rejection(known)
          : !real.ok
            ? real.reason === 'too-close'
              ? lowerFirst(hiddenCityText(ctx.rules))
              : rejection(real)
            : u.pos.q !== target.q || u.pos.r !== target.r || u.inside
              ? unreachable
              : null;
    }
    texts.push(why ? `Couldn't found a city there: ${why}` : "Couldn't found a city there");
  }
  return texts;
}

/** "Too close to City 2" → "too close to City 2", after a colon. */
function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}

/** A toast under the timer: a refused click (warn) or a landed one (ok). */
export interface Toast {
  id: number;
  text: string;
  tone: 'ok' | 'warn';
  /** How many identical toasts it stands for ("×3"). */
  count: number;
  /** When it goes (ms, `Date.now()` clock); refreshed by each repeat. */
  until: number;
}

/**
 * How long a toast stays up. Fixed: a repeat ("×3") restarts the clock, so
 * every toast lasts this long from its latest occurrence.
 */
export const TOAST_MS = 2000;

/** The icon leading a toast, by tone (icon, then text). Text glyphs, not emoji. */
export const TOAST_ICONS: Record<Toast['tone'], string> = { ok: '✓', warn: '⚠\uFE0E' };

/**
 * Adds toasts to the shown ones: a repeat of one already up (same text and
 * tone) counts on it and refreshes its timer in place instead of stacking;
 * the `max` most recently shown or refreshed are kept, in shown order.
 */
export function mergeToasts(
  prev: readonly Toast[],
  added: ReadonlyArray<Pick<Toast, 'text' | 'tone'>>,
  now: number,
  lifetime: number,
  nextId: () => number,
  max = 3
): Toast[] {
  const next = [...prev];
  for (const a of added) {
    const i = next.findIndex((t) => t.text === a.text && t.tone === a.tone);
    if (i >= 0) next[i] = { ...next[i], count: next[i].count + 1, until: now + lifetime };
    else next.push({ id: nextId(), text: a.text, tone: a.tone, count: 1, until: now + lifetime });
  }
  // Evict the least recently refreshed, not the earliest inserted, so a
  // repeat that was just counted on stays up; shown order is kept.
  while (next.length > max) {
    let oldest = 0;
    for (let j = 1; j < next.length; j++) if (next[j].until < next[oldest].until) oldest = j;
    next.splice(oldest, 1);
  }
  return next;
}
